import { getModel } from '@smx-ui-model/store-model';
import { ModelType } from '@smx-ui-model/types';
import { WrapperOneLoaderQuery } from '@smx-ui-table/wrapper-one-loader-query';
import { Alert, Flex, Progress, Spin } from 'antd';
import { Dispatch, SetStateAction, Suspense, useEffect, useRef, useState } from 'react';

import {
    drawingSetJobFragment$data,
    DrawingSetJobStageType,
    JobStatus,
} from '../graphql/__generated__/drawingSetJobFragment.graphql';
import { DrawingSetJobModelType, StoreDrawingsModelType } from '../model/types';

interface TableDrawingSetUploadProcessProps {
    jobId: string;
    setJobStatus: Dispatch<SetStateAction<JobStatus | undefined>>;
    modeView?: 'big' | 'small';
    timeout?: number;
}

export const DrawingSetUploadProcess = ({
    jobId,
    setJobStatus,
    modeView = 'big',
    timeout = 2000,
}: TableDrawingSetUploadProcessProps) => {
    const [render, setRender] = useState<number>(0);
    const timerInterval = useRef<number | NodeJS.Timeout>(undefined);
    const currentStatusRef = useRef<Partial<{ [key in Partial<DrawingSetJobStageType>]: number }>>({
        UPLOADING: 0,
        EXTRACTING_ARTIFACTS: 0,
        EXTRACTING_SHEET_INFO: 0,
        RASTERIZING: 0,
        SHEET_SPLITTING: 0,
    });

    const dataSourceRef = useRef<drawingSetJobFragment$data>(undefined);
    const model = getModel<ModelType<DrawingSetJobModelType>, StoreDrawingsModelType>('drawingSetJob');

    useEffect(() => {
        setTimeout(() => {
            setRender((prevValue) => prevValue + 1);
        }, 300);

        timerInterval.current = setInterval(() => {
            setRender((prevValue) => prevValue + 1);
        }, timeout);

        return () => {
            clearInterval(timerInterval.current);
        };
    }, []);

    const jobStatus = dataSourceRef.current?.jobStatus;

    useEffect(() => {
        if (dataSourceRef.current) {
            setJobStatus(jobStatus);
            if (jobStatus === 'FAILED' || jobStatus === 'SUCCESS') {
                clearInterval(timerInterval.current);
            }
        }
    }, [render]);

    switch (dataSourceRef.current?.stage) {
        case 'UPLOADING':
        case 'SHEET_SPLITTING':
            currentStatusRef.current = {
                ...currentStatusRef.current,
                SHEET_SPLITTING: Math.round(dataSourceRef.current.stageProgress),
            };
            break;
        case 'RASTERIZING':
            currentStatusRef.current = {
                ...currentStatusRef.current,
                SHEET_SPLITTING: 100,
                RASTERIZING: Math.round(dataSourceRef.current.stageProgress),
            };
            break;
        case 'EXTRACTING_ARTIFACTS':
            currentStatusRef.current = {
                ...currentStatusRef.current,
                SHEET_SPLITTING: 100,
                RASTERIZING: 100,
                EXTRACTING_ARTIFACTS: Math.round(dataSourceRef.current.stageProgress),
            };
            break;
        case 'EXTRACTING_SHEET_INFO':
            currentStatusRef.current = {
                ...currentStatusRef.current,
                SHEET_SPLITTING: 100,
                RASTERIZING: 100,
                EXTRACTING_ARTIFACTS: 100,
                EXTRACTING_SHEET_INFO: Math.round(dataSourceRef.current.stageProgress),
            };
            break;
    }

    const getCurrentStatus = (currentStatus: number) => {
        return currentStatus < 100 ? 'active' : 'success';
    };

    const getMessageAlert = (stage: DrawingSetJobStageType) => {
        switch (stage) {
            case 'UPLOADING':
                return 'Problem with uploading file';
        }
        return 'Something went wrong';
    };

    const loader = (
        <Suspense>
            <WrapperOneLoaderQuery
                fetchKey={render}
                model={model}
                variables={{ id: jobId || '78acd971-2500-479b-bef0-a6a0971a7af6' }}
                dataSourceRef={dataSourceRef}
            />
        </Suspense>
    );

    return (
        <Spin spinning={!dataSourceRef.current}>
            {modeView === 'big' && (
                <Flex vertical style={{ padding: '0 15px' }}>
                    {loader}
                    {dataSourceRef.current?.jobStatus === 'FAILED' ? (
                        <Alert
                            message="Failed"
                            description={getMessageAlert(dataSourceRef.current?.stage)}
                            type="error"
                            showIcon
                        />
                    ) : (
                        <>
                            <Flex justify={'space-between'} align={'center'}>
                                <h4>Loading process ...</h4>{' '}
                                <h4>{Math.round(dataSourceRef.current?.jobProgress || 0)}%</h4>
                            </Flex>
                            Sheet splitting ...
                            <Progress
                                percent={currentStatusRef.current.SHEET_SPLITTING}
                                status={getCurrentStatus(currentStatusRef.current.SHEET_SPLITTING!)}
                            />
                            Rasterizing ...
                            <Progress
                                percent={currentStatusRef.current.RASTERIZING}
                                status={getCurrentStatus(currentStatusRef.current.RASTERIZING!)}
                            />
                            Extracting artifacts ...
                            <Progress
                                percent={currentStatusRef.current.EXTRACTING_ARTIFACTS}
                                status={getCurrentStatus(currentStatusRef.current.EXTRACTING_ARTIFACTS!)}
                            />
                            Extracting sheet info ...
                            <Progress
                                percent={currentStatusRef.current.EXTRACTING_SHEET_INFO}
                                status={getCurrentStatus(currentStatusRef.current.EXTRACTING_SHEET_INFO!)}
                            />
                        </>
                    )}
                </Flex>
            )}

            {modeView === 'small' && (
                <Flex style={{ width: 30 }}>
                    {loader}
                    {jobStatus === 'RUNNING' && (
                        <Progress
                            style={{ position: 'absolute', top: -4 }}
                            type="circle"
                            percent={Math.round(dataSourceRef.current?.jobProgress || 0)}
                            size={30}
                        />
                    )}
                </Flex>
            )}
        </Spin>
    );
};
